import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Clock, CheckCircle2, LogOut, WifiOff, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { useSelector } from "react-redux";
import { StoreState } from "../../models/reduxModels";
import IPhoneCameraModal from "./IPhoneCameraModal";
import IPhoneApprovalModal from "./IPhoneApprovalModal";
import axios from "axios";
import { baseUrl, environment } from "../../environment";
import moment from "moment";
import { useSnackbar } from "notistack";
import {
  createUnifiedArray,
  getOffWeekdaysFromShift,
  calculateDuration,
  UnifiedEntry,
} from "../../utility/monthlyAttendance";
import { Cookies } from "react-cookie";
import { useDispatch } from "react-redux";
import { ApiCallErrorAction } from "../../stores/actions/apiStatusActions";
import { LogOutAction } from "../../stores/actions/userAction";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const IPhoneDashboard: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const userDetails = useSelector((state: StoreState) => state.user.userDetails);

  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [captureMode, setCaptureMode] = useState<"checkin" | "checkout">("checkin");
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [rawAttendanceData, setRawAttendanceData] = useState<any>(null);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const dispatch = useDispatch()

  // Month & Year Picker Modal
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState<number>(new Date().getFullYear());

  // Approval Modal States
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalDateStr, setApprovalDateStr] = useState<string>("");
  const [approvalTargetItem, setApprovalTargetItem] = useState<any>(null);

  const selectedMonth = currentDate.getMonth(); // 0-based
  const selectedYear = currentDate.getFullYear();

  // Online / Offline listener
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Fetch live fresh dashboard & monthly attendance details (NO CACHE)
  const fetchDashboardDetails = useCallback(async () => {
    if (!userDetails?.accessToken) return;
    try {
      // 1. Fetch live app-dashboard status (NO CACHE)
      const response = await axios.get(
        baseUrl + environment.urls.appDashboard,
        {
          headers: {
            Authorization: `Bearer ${userDetails.accessToken}`,
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
        }
      );
      if (response.status === 200 && response.data) {
        setDashboardData(response.data);
      }

      // 2. Fetch monthly attendance payload (NO CACHE)
      const monthNum = (selectedMonth + 1).toString();
      const yearNum = selectedYear.toString();

      const monthRes = await axios.post(
        baseUrl + environment.urls.myAttendance,
        {
          month: monthNum,
          year: yearNum,
          employee_id: userDetails.employeeId,
        },
        {
          headers: {
            Authorization: `Bearer ${userDetails.accessToken}`,
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
        }
      );

      if (monthRes.status === 200 && monthRes.data) {
        setRawAttendanceData(monthRes.data);
      }
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === "string" ? error.response.data : undefined);

      if (error.response?.status === 401) {
        let cook = new Cookies();
        dispatch(
          ApiCallErrorAction({
            errorCode: error?.response?.data?.errorCode || "",
            message: backendMessage || "Please Login again to continue.",
          })
        );
        cook.remove("user", { path: "/" });
        dispatch(LogOutAction());
      } else if (error.response?.status === 500) {
        dispatch(
          ApiCallErrorAction({
            errorCode: error?.response?.data?.errorCode || "",
            message: backendMessage || "Internal Server Error. Please try again later.",
          })
        );
      } else {
        dispatch(
          ApiCallErrorAction({
            errorCode: error?.response?.data?.errorCode || "",
            message: backendMessage || "Error encountered please try again later",
          })
        );
      }
      console.error("Error fetching live attendance dashboard details:", error);
    }
  }, [selectedMonth, selectedYear, userDetails]);

  useEffect(() => {
    fetchDashboardDetails();
  }, [fetchDashboardDetails]);

  // Compute off weekdays dynamically based on backend shift rule
  const offWeekdays = useMemo(() => {
    return getOffWeekdaysFromShift(rawAttendanceData?.shift);
  }, [rawAttendanceData]);

  // Compute unified entries for current month
  const unifiedEntries = useMemo(() => {
    try {
      return createUnifiedArray(rawAttendanceData || {}, selectedYear, selectedMonth);
    } catch {
      return [];
    }
  }, [rawAttendanceData, selectedYear, selectedMonth]);

  // Map entries by date string (YYYY-MM-DD)
  const dateMap = useMemo(() => {
    const map = new Map<string, UnifiedEntry>();
    if (unifiedEntries && Array.isArray(unifiedEntries)) {
      unifiedEntries.forEach((item) => {
        if (item.date) {
          map.set(moment(item.date).format("YYYY-MM-DD"), item);
        }
      });
    }
    return map;
  }, [unifiedEntries]);

  // Check 2-day approval eligibility window
  const isEligibleForApproval = (dateStr: string): boolean => {
    const today = moment().startOf("day");
    const target = moment(dateStr, "YYYY-MM-DD").startOf("day");
    const diff = today.diff(target, "days");
    return diff >= 0 && diff <= 2;
  };

  // Open Approval Modal from Calendar Cell (for status 2 / Get Appr or absent days within eligibility window)
  const openApprovalModal = (dateStr: string, itemData?: any) => {
    const isStatus2GetAppr =
      itemData?.type === "attendance" &&
      itemData?.original?.status === 2 &&
      itemData?.original?.is_present;

    const isAbsentDay = itemData?.type === "absent";

    if (!isStatus2GetAppr && !isAbsentDay) {
      return;
    }

    if (isAbsentDay) {
      enqueueSnackbar("No attendance was recorded for this day. For full absent days, please submit a Leave Application.", {
        variant: "info",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }

    if (!isEligibleForApproval(dateStr)) {
      enqueueSnackbar("Approval request is available only for dates within 2 days prior to today.", {
        variant: "info",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }

    setApprovalDateStr(dateStr);
    setApprovalTargetItem(itemData);
    setShowApprovalModal(true);
  };

  // Robust checkin parsing
  const isCheckedIn =
    Number(dashboardData?.isCheckin) === 1 ||
    dashboardData?.isCheckin === true ||
    dashboardData?.is_checkin === 1;

  const checkinTimeStr =
    dashboardData?.checkinTime ||
    dashboardData?.checkin_time ||
    dashboardData?.in_time;

  const checkoutTimeStr =
    dashboardData?.checkoutTime ||
    dashboardData?.checkout_time ||
    dashboardData?.out_time;

  // Live timer interval: Starts on Clock In, Stops on Clock Out
  useEffect(() => {
    if (!isCheckedIn || !checkinTimeStr) {
      setElapsedSeconds(0);
      return;
    }

    const checkinMoment = moment(checkinTimeStr, [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss",
      "HH:mm:ss",
      moment.ISO_8601,
    ]);

    const interval = setInterval(() => {
      const now = moment();
      const diff = now.diff(checkinMoment, "seconds");
      setElapsedSeconds(diff > 0 ? diff : 0);
    }, 1000);

    return () => clearInterval(interval);
  }, [isCheckedIn, checkinTimeStr]);

  const formatTimer = (totalSecs: number) => {
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOpenClockModal = () => {
    if (!isOnline) return;
    setCaptureMode(isCheckedIn ? "checkout" : "checkin");
    setCameraModalOpen(true);
  };

  // Month navigation handlers
  const handlePrevMonth = () => {
    const prev = new Date(selectedYear, selectedMonth - 1, 1);
    setCurrentDate(prev);
    setPickerYear(prev.getFullYear());
  };

  const handleNextMonth = () => {
    const next = new Date(selectedYear, selectedMonth + 1, 1);
    setCurrentDate(next);
    setPickerYear(next.getFullYear());
  };

  // Render Day Cell Badge
  const renderCellBadge = (dateStr: string, entry?: UnifiedEntry) => {
    const isFuture = moment(dateStr, "YYYY-MM-DD").isAfter(moment(), "day");

    if (!entry) {
      if (isFuture) return null;
      return <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1 py-0.5 rounded-md">Absent</span>;
    }

    const type = entry.type;
    const orig = entry.original || {};

    if (type === "weekend") {
      return <span className="text-[8px] sm:text-[9px] font-bold text-gray-500 bg-gray-100 px-1 py-0.5 rounded-md">Weekend Off</span>;
    } else if (type === "holiday") {
      return <span className="text-[8px] sm:text-[9px] font-bold text-blue-600 bg-blue-50 px-1 py-0.5 rounded-md truncate max-w-[42px] sm:max-w-[50px]">{orig.holiday_name || "Holiday"}</span>;
    } else if (type === "leave") {
      return <span className="text-[8px] sm:text-[9px] font-bold text-amber-600 bg-amber-50 px-1 py-0.5 rounded-md truncate max-w-[42px] sm:max-w-[50px]">{orig.title || orig.leave_type_name || "On Leave"}</span>;
    }

    if (isFuture) return null;

    if (type === "attendance") {
      if ((orig.is_present || orig.upd_in_time) && [1, 5].includes(orig.status)) {
        const duration = calculateDuration(
          orig.upd_in_time || orig.in_time,
          orig.upd_out_time || orig.out_time
        );
        return (
          <span className="text-[8px] sm:text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded-md border border-emerald-200/60">
            {duration || "Present"}
          </span>
        );
      } else if (orig.is_present && orig.status === 2) {
        return <span className="text-[8px] sm:text-[9px] font-bold text-yellow-800 bg-yellow-100 px-1 py-0.5 rounded-md">Get Appr</span>;
      } else if (orig.is_present && orig.status === 3) {
        return <span className="text-[8px] sm:text-[9px] font-bold text-blue-800 bg-blue-100 px-1 py-0.5 rounded-md">Wait Appr</span>;
      } else if (orig.is_present && orig.status === 5) {
        return <span className="text-[8px] sm:text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded-md">Present</span>;
      } else {
        return <span className="text-[8px] sm:text-[9px] font-bold text-rose-600 bg-rose-50 px-1 py-0.5 rounded-md">Absent</span>;
      }
    }

    return <span className="text-[8px] sm:text-[9px] font-bold text-rose-600 bg-rose-50 px-1 py-0.5 rounded-md">Absent</span>;
  };

  const daysInMonthCount = moment(currentDate).daysInMonth();
  const firstDayOfWeek = moment(new Date(selectedYear, selectedMonth, 1)).day();

  return (
    <div className="w-full space-y-3.5 sm:space-y-4 font-sans text-gray-900">
      {/* Offline Warning Banner */}
      {!isOnline && (
        <div className="bg-rose-500 text-white text-xs px-3 py-2 rounded-2xl flex items-center justify-center gap-2 shadow-xs">
          <WifiOff className="w-4 h-4 shrink-0" />
          <span className="text-[11px] sm:text-xs">You are offline. Attendance operations are disabled.</span>
        </div>
      )}

      {/* Attendance Timer Card (Matching Mobile App saffron design) */}
      <div className="bg-gradient-to-br from-[#FF6B1A] to-[#EA580C] rounded-3xl p-5 sm:p-6 text-white shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 sm:w-36 sm:h-36 bg-white/10 rounded-full blur-xl pointer-events-none" />

        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] sm:text-xs text-white/80 font-medium uppercase tracking-wider">
              Attendance Status
            </p>
            <h2 className="text-base sm:text-lg font-bold">
              {isCheckedIn ? "Currently Checked In" : "Not Checked In"}
            </h2>
          </div>
          <span
            className={`px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-bold border ${isCheckedIn
              ? "bg-emerald-500/20 text-emerald-100 border-emerald-300/30"
              : "bg-white/20 text-white border-white/30"
              }`}
          >
            {isCheckedIn ? "Active Shift" : "Shift Ended"}
          </span>
        </div>

        {/* Clock Timer Display */}
        <div className="my-3 py-3 px-2 bg-black/15 rounded-2xl border border-white/10 backdrop-blur-xs flex flex-col items-center justify-center">
          <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white/80 mb-1" />
          <span className="text-3xl sm:text-4xl font-extrabold tracking-tight font-mono">
            {formatTimer(elapsedSeconds)}
          </span>
          <p className="text-[10px] sm:text-[11px] text-white/70 mt-1">
            {moment().format("dddd, DD MMMM YYYY")}
          </p>
        </div>

        {/* Clock In / Out Action Button */}
        <button
          onClick={handleOpenClockModal}
          disabled={!isOnline}
          className={`w-full py-3.5 rounded-2xl font-bold text-xs sm:text-sm shadow-md flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${!isOnline
            ? "bg-gray-400 text-gray-200 cursor-not-allowed"
            : isCheckedIn
              ? "bg-rose-600 hover:bg-rose-700 text-white"
              : "bg-white text-[#FF6B1A] hover:bg-orange-50"
            }`}
        >
          <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
          <span>{isCheckedIn ? "Clock Out Now" : "Clock In Now"}</span>
        </button>
      </div>

      {/* Quick Info Grid */}
      <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
        <div className="bg-white rounded-2xl p-3.5 sm:p-4 shadow-xs border border-gray-100 flex items-center space-x-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-500 font-medium">Clock In</p>
            <p className="text-xs font-bold text-gray-900 truncate">
              {checkinTimeStr
                ? moment(checkinTimeStr, [
                  "YYYY-MM-DD HH:mm:ss",
                  "YYYY-MM-DDTHH:mm:ss",
                  "HH:mm:ss",
                  moment.ISO_8601,
                ]).format("hh:mm A")
                : "--:--"}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-3.5 sm:p-4 shadow-xs border border-gray-100 flex items-center space-x-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
            <LogOut className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-500 font-medium">Clock Out</p>
            <p className="text-xs font-bold text-gray-900 truncate">
              {checkoutTimeStr
                ? moment(checkoutTimeStr, [
                  "YYYY-MM-DD HH:mm:ss",
                  "YYYY-MM-DDTHH:mm:ss",
                  "HH:mm:ss",
                  moment.ISO_8601,
                ]).format("hh:mm A")
                : "--:--"}
            </p>
          </div>
        </div>
      </div>

      {/* Calendar Section (Matching Mobile App DashboardAttendanceCalendar.tsx) */}
      <div className="bg-white rounded-3xl p-4 sm:p-5 shadow-xs border border-gray-100">
        {/* Calendar Header Bar */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setShowMonthYearPicker(true)}
            className="flex items-center space-x-1 px-3 py-1.5 rounded-xl bg-gray-100 hover:bg-gray-200/80 transition-colors text-xs font-bold text-gray-900"
          >
            <span>
              {MONTHS[selectedMonth]} {selectedYear}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-600" />
          </button>

          <div className="flex items-center space-x-1">
            <button
              onClick={handlePrevMonth}
              className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <button
              onClick={() => {
                const now = new Date();
                setCurrentDate(now);
                setPickerYear(now.getFullYear());
              }}
              className="px-2.5 py-1 rounded-lg bg-[#FF6B1A]/10 text-[#FF6B1A] text-[11px] font-bold hover:bg-[#FF6B1A]/20"
            >
              Today
            </button>

            <button
              onClick={handleNextMonth}
              className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Short Form Legend Bar */}
        <div className="flex items-center space-x-3 text-[10px] text-gray-500 mb-3 bg-gray-50 p-2 rounded-xl border border-gray-100">
          <div className="flex items-center space-x-1">
            <span className="px-1 py-0.5 rounded-md bg-yellow-100 text-yellow-800 font-bold">
              Get Appr
            </span>
            <span>: Get Approval</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="px-1 py-0.5 rounded-md bg-blue-100 text-blue-800 font-bold">
              Wait Appr
            </span>
            <span>: Waiting For Approval</span>
          </div>
        </div>

        {/* Weekday Headers */}
        <div className="grid grid-cols-7 gap-1 text-center mb-1">
          {WEEKDAYS.map((day, idx) => (
            <div
              key={day}
              className={`font-semibold py-1 text-[10px] sm:text-xs ${offWeekdays.includes(idx) ? "text-rose-500 font-bold" : "text-gray-400"
                }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days Grid */}
        <div className="grid grid-cols-7 gap-1 text-center">
          {/* Empty Padding Slots */}
          {Array.from({ length: firstDayOfWeek }).map((_, idx) => (
            <div key={`empty-${idx}`} className="py-2" />
          ))}

          {/* Month Day Cells */}
          {Array.from({ length: daysInMonthCount }).map((_, idx) => {
            const dayNum = idx + 1;
            const cellDate = new Date(selectedYear, selectedMonth, dayNum);
            const dateStr = moment(cellDate).format("YYYY-MM-DD");
            const isToday = dateStr === moment().format("YYYY-MM-DD");
            const entry = dateMap.get(dateStr);
            const isStatus2GetAppr =
              entry?.type === "attendance" &&
              entry?.original?.status === 2 &&
              entry?.original?.is_present;

            return (
              <div
                key={dayNum}
                onClick={() => {
                  if (isStatus2GetAppr) {
                    openApprovalModal(dateStr, entry);
                  }
                }}
                className={`min-h-[48px] sm:min-h-[54px] p-1 rounded-xl flex flex-col items-center justify-between border transition-all ${isStatus2GetAppr ? "cursor-pointer hover:border-[#FF6B1A]" : "cursor-default"
                  } ${isToday
                    ? "border-[#FF6B1A] bg-[#FFF3EB]/40 shadow-xs"
                    : "border-gray-100 bg-white"
                  }`}
              >
                <span
                  className={`text-[11px] font-bold ${isToday ? "text-[#FF6B1A]" : "text-gray-800"
                    }`}
                >
                  {dayNum}
                </span>

                <div className="w-full flex items-center justify-center">
                  {renderCellBadge(dateStr, entry)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Month & Year Selection Modal */}
      {showMonthYearPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fadeIn">
          <div className="w-full max-w-xs bg-white rounded-3xl p-5 border border-gray-100 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Select Date, Month & Year</h3>
              <button
                onClick={() => setShowMonthYearPicker(false)}
                className="text-xs font-bold text-gray-400 hover:text-gray-600"
              >
                Close
              </button>
            </div>

            {/* Direct Date Picker Input */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                Pick Specific Date
              </label>
              <input
                type="date"
                value={moment(currentDate).format("YYYY-MM-DD")}
                onChange={(e) => {
                  if (e.target.value) {
                    const selected = moment(e.target.value).toDate();
                    setCurrentDate(selected);
                    setPickerYear(selected.getFullYear());
                    setShowMonthYearPicker(false);
                  }
                }}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 font-bold focus:outline-none focus:border-[#FF6B1A]"
              />
            </div>

            <div className="relative flex py-1 items-center">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="flex-shrink mx-2 text-[10px] font-bold text-gray-400 uppercase">Or Select Month & Year</span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>

            {/* Year Selector */}
            <div className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded-xl">
              <button
                onClick={() => setPickerYear((y) => y - 1)}
                className="p-1 text-gray-600 hover:bg-gray-200 rounded-lg"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-bold text-gray-900">{pickerYear}</span>
              <button
                onClick={() => setPickerYear((y) => y + 1)}
                className="p-1 text-gray-600 hover:bg-gray-200 rounded-lg"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Months Grid */}
            <div className="grid grid-cols-3 gap-2">
              {MONTHS.map((mName, mIdx) => (
                <button
                  key={mName}
                  onClick={() => {
                    const nextDate = new Date(pickerYear, mIdx, 1);
                    setCurrentDate(nextDate);
                    setShowMonthYearPicker(false);
                  }}
                  className={`py-2 text-xs font-semibold rounded-xl transition-all ${mIdx === selectedMonth && pickerYear === selectedYear
                    ? "bg-[#FF6B1A] text-white shadow-xs"
                    : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  {mName.substring(0, 3)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Send For Approval Modal */}
      <IPhoneApprovalModal
        open={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        approvalDateStr={approvalDateStr}
        targetItem={approvalTargetItem}
        onSuccess={fetchDashboardDetails}
      />

      {/* Camera Modal Component */}
      <IPhoneCameraModal
        open={cameraModalOpen}
        onClose={() => setCameraModalOpen(false)}
        mode={captureMode}
        onSuccess={fetchDashboardDetails}
      />
    </div>
  );
};

export default IPhoneDashboard;
